// Define a type for objects that have HATEOAS links
import { ServiceOpts, useClient } from "./client";
import {
    RequestOptions as RequestOptionsBase,
    ResponseStyle,
} from "./gen/client/types";
import * as types from "./gen/types.gen";

type WithLinks = {
    links?: Record<string, types.Hateoas>;
};

type RequestOptions = Omit<RequestOptionsBase<ResponseStyle, false>, "method"> &
    Pick<Required<RequestOptionsBase<ResponseStyle, false>>, "method">;

function mergeSearchParams(href: string, searchParams?: URLSearchParams) {
    const url = new URL(new Request(href).url);
    return new URLSearchParams({
        ...Object.fromEntries(url.searchParams),
        ...Object.fromEntries(searchParams || new URLSearchParams()),
    });
}

export async function followLink<ExpectedData>(
    object: WithLinks,
    key: string,
    body?:
        | RequestInit["body"]
        | Record<string, unknown>
        | Array<Record<string, unknown>>
        | Array<unknown>
        | number,
    signal?: AbortSignal,
    query?: URLSearchParams,
    serviceOptions: ServiceOpts = {
        requires: {
            auth: true,
            organization: true,
            stek: true,
        },
    }
) {
    const link = object.links?.[key];
    if (!link) {
        throw new Error(`Link with key '${key}' not found`);
    }

    const baseUrl = import.meta.env.VITE_API_URL;
    const url = new URL(link.href, baseUrl);
    url.search = mergeSearchParams(link.href, query).toString();
    // Get relative URL
    const href = url.toString().replace(new RegExp("^" + baseUrl), "");

    const options: RequestOptions = {
        url: href,
        ...signal,
        method: (link.type as RequestOptions["method"]) || "GET",
    };

    if (body) {
        options.body = body;
    }

    const client = useClient(serviceOptions);

    return await client.request<ExpectedData>(options);
}
